import { create } from 'zustand';
import type {
  CrawlJob,
  CrawlOptionInfo,
  CrawlSchedule,
  CrawlUrlNode,
  WithResourceId,
} from '../../../types/knowledge';
import {
  CRAWL_DEFAULT_ALLOW_SUBDOMAINS,
  CRAWL_DEFAULT_MAX_DEPTH,
  CRAWL_DEFAULT_MAX_PAGES,
  CRAWL_DEFAULT_USE_START_URL_AS_PREFIX_FILTER,
} from '../crawlStartDefaults';

type KnowledgeCrawlsTabStore = {
  crawlUrl: string;
  crawlMaxDepth: string;
  crawlMaxPages: string;
  crawlUseStartUrlAsPrefixFilter: boolean;
  crawlAllowSubdomains: boolean;
  crawlExcludedUrls: string[];
  crawlOptionInfo: CrawlOptionInfo | null;

  selectionCrawl: WithResourceId<CrawlJob> | null;
  selectedNodeIds: string[];
  selectionNodes: Array<WithResourceId<CrawlUrlNode>>;
  isSelectionSubmitting: boolean;

  isMappingExpanded: boolean;
  isNeedsAttentionExpanded: boolean;
  isInProgressExpanded: boolean;
  isCompletedExpanded: boolean;
  isFailedExpanded: boolean;

  scheduleModalCrawl: WithResourceId<CrawlJob> | null;
  isScheduleSubmitting: boolean;
  pendingScheduleDelete: WithResourceId<CrawlSchedule> | null;

  setCrawlUrl: (crawlUrl: string) => void;
  setCrawlMaxDepth: (crawlMaxDepth: string) => void;
  setCrawlMaxPages: (crawlMaxPages: string) => void;
  setCrawlUseStartUrlAsPrefixFilter: (
    crawlUseStartUrlAsPrefixFilter: boolean,
  ) => void;
  setCrawlAllowSubdomains: (crawlAllowSubdomains: boolean) => void;
  setCrawlExcludedUrls: (crawlExcludedUrls: string[]) => void;
  setCrawlOptionInfo: (crawlOptionInfo: CrawlOptionInfo | null) => void;
  resetCrawlForm: () => void;

  openCrawlSelection: (crawl: WithResourceId<CrawlJob>) => void;
  setSelectionData: (nodes: Array<WithResourceId<CrawlUrlNode>>) => void;
  setSelectedNodeIds: (selectedNodeIds: string[]) => void;
  setIsSelectionSubmitting: (isSelectionSubmitting: boolean) => void;
  closeCrawlSelection: () => void;

  setIsMappingExpanded: (isExpanded: boolean) => void;
  setIsNeedsAttentionExpanded: (isExpanded: boolean) => void;
  setIsInProgressExpanded: (isExpanded: boolean) => void;
  setIsCompletedExpanded: (isExpanded: boolean) => void;
  setIsFailedExpanded: (isExpanded: boolean) => void;
  toggleMappingExpanded: () => void;
  toggleNeedsAttentionExpanded: () => void;
  toggleInProgressExpanded: () => void;
  toggleCompletedExpanded: () => void;
  toggleFailedExpanded: () => void;
  expandAllCrawlGroups: () => void;
  collapseAllCrawlGroups: () => void;

  openScheduleModal: (crawl: WithResourceId<CrawlJob>) => void;
  closeScheduleModal: () => void;
  setIsScheduleSubmitting: (isScheduleSubmitting: boolean) => void;
  setPendingScheduleDelete: (
    schedule: WithResourceId<CrawlSchedule> | null,
  ) => void;

  reset: () => void;
};

const initialCrawlFormState = {
  crawlUrl: '',
  crawlMaxDepth: String(CRAWL_DEFAULT_MAX_DEPTH),
  crawlMaxPages: String(CRAWL_DEFAULT_MAX_PAGES),
  crawlUseStartUrlAsPrefixFilter: CRAWL_DEFAULT_USE_START_URL_AS_PREFIX_FILTER,
  crawlAllowSubdomains: CRAWL_DEFAULT_ALLOW_SUBDOMAINS,
  crawlExcludedUrls: [] as string[],
  crawlOptionInfo: null as CrawlOptionInfo | null,
};

const initialSelectionState = {
  selectionCrawl: null as WithResourceId<CrawlJob> | null,
  selectedNodeIds: [] as string[],
  selectionNodes: [] as Array<WithResourceId<CrawlUrlNode>>,
  isSelectionSubmitting: false,
};

export const useKnowledgeCrawlsTabStore = create<KnowledgeCrawlsTabStore>(
  (set) => ({
    ...initialCrawlFormState,
    ...initialSelectionState,

    isMappingExpanded: true,
    isNeedsAttentionExpanded: true,
    isInProgressExpanded: true,
    isCompletedExpanded: true,
    isFailedExpanded: true,

    scheduleModalCrawl: null,
    isScheduleSubmitting: false,
    pendingScheduleDelete: null,

    setCrawlUrl: (crawlUrl) => set({ crawlUrl }),
    setCrawlMaxDepth: (crawlMaxDepth) => set({ crawlMaxDepth }),
    setCrawlMaxPages: (crawlMaxPages) => set({ crawlMaxPages }),
    setCrawlUseStartUrlAsPrefixFilter: (crawlUseStartUrlAsPrefixFilter) =>
      set({ crawlUseStartUrlAsPrefixFilter }),
    setCrawlAllowSubdomains: (crawlAllowSubdomains) =>
      set({ crawlAllowSubdomains }),
    setCrawlExcludedUrls: (crawlExcludedUrls) => set({ crawlExcludedUrls }),
    setCrawlOptionInfo: (crawlOptionInfo) => set({ crawlOptionInfo }),
    resetCrawlForm: () => set(initialCrawlFormState),

    openCrawlSelection: (crawl) =>
      set({
        selectionCrawl: crawl,
        selectionNodes: [],
        selectedNodeIds: [],
        isSelectionSubmitting: false,
      }),
    setSelectionData: (nodes) =>
      set({
        selectionNodes: nodes,
        selectedNodeIds: nodes
          .filter((node) => node.selected)
          .map((node) => node.id),
      }),
    setSelectedNodeIds: (selectedNodeIds) => set({ selectedNodeIds }),
    setIsSelectionSubmitting: (isSelectionSubmitting) =>
      set({ isSelectionSubmitting }),
    closeCrawlSelection: () => set(initialSelectionState),

    setIsMappingExpanded: (isMappingExpanded) => set({ isMappingExpanded }),
    setIsNeedsAttentionExpanded: (isNeedsAttentionExpanded) =>
      set({ isNeedsAttentionExpanded }),
    setIsInProgressExpanded: (isInProgressExpanded) =>
      set({ isInProgressExpanded }),
    setIsCompletedExpanded: (isCompletedExpanded) =>
      set({ isCompletedExpanded }),
    setIsFailedExpanded: (isFailedExpanded) => set({ isFailedExpanded }),
    toggleMappingExpanded: () =>
      set((state) => ({
        isMappingExpanded: !state.isMappingExpanded,
      })),
    toggleNeedsAttentionExpanded: () =>
      set((state) => ({
        isNeedsAttentionExpanded: !state.isNeedsAttentionExpanded,
      })),
    toggleInProgressExpanded: () =>
      set((state) => ({
        isInProgressExpanded: !state.isInProgressExpanded,
      })),
    toggleCompletedExpanded: () =>
      set((state) => ({
        isCompletedExpanded: !state.isCompletedExpanded,
      })),
    toggleFailedExpanded: () =>
      set((state) => ({
        isFailedExpanded: !state.isFailedExpanded,
      })),
    expandAllCrawlGroups: () =>
      set({
        isMappingExpanded: true,
        isNeedsAttentionExpanded: true,
        isInProgressExpanded: true,
        isCompletedExpanded: true,
        isFailedExpanded: true,
      }),
    collapseAllCrawlGroups: () =>
      set({
        isMappingExpanded: false,
        isNeedsAttentionExpanded: false,
        isInProgressExpanded: false,
        isCompletedExpanded: false,
        isFailedExpanded: false,
      }),

    openScheduleModal: (scheduleModalCrawl) =>
      set({ scheduleModalCrawl, pendingScheduleDelete: null }),
    closeScheduleModal: () =>
      set({ scheduleModalCrawl: null, pendingScheduleDelete: null }),
    setIsScheduleSubmitting: (isScheduleSubmitting) =>
      set({ isScheduleSubmitting }),
    setPendingScheduleDelete: (pendingScheduleDelete) =>
      set({ pendingScheduleDelete }),

    reset: () =>
      set({
        ...initialCrawlFormState,
        ...initialSelectionState,
        scheduleModalCrawl: null,
        isScheduleSubmitting: false,
        pendingScheduleDelete: null,
      }),
  }),
);
